import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';

type PreferredMethod = 'email' | 'sms' | null;

interface NotificationPrefs {
  emailEnabled: boolean;
  smsEnabled: boolean;
  preferredMethod: PreferredMethod;
}

const LOCAL_STORAGE_KEY = 'gc_notification_prefs';

const defaultPrefs: NotificationPrefs = {
  emailEnabled: true,
  smsEnabled: true,
  preferredMethod: 'email',
};

const resolvePreferredMethod = (
  emailEnabled: boolean,
  smsEnabled: boolean,
  preferred: PreferredMethod | null,
): PreferredMethod => {
  if (preferred === 'email' && emailEnabled) return 'email';
  if (preferred === 'sms' && smsEnabled) return 'sms';
  if (emailEnabled) return 'email';
  if (smsEnabled) return 'sms';
  return null;
};

const readPrefsFromStorage = (): NotificationPrefs => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as NotificationPrefs;
      return {
        emailEnabled: Boolean(parsed.emailEnabled),
        smsEnabled: Boolean(parsed.smsEnabled),
        preferredMethod: resolvePreferredMethod(
          Boolean(parsed.emailEnabled),
          Boolean(parsed.smsEnabled),
          parsed.preferredMethod ?? null,
        ),
      };
    }
  } catch (err) {
    console.warn('Unable to parse cached notification preferences. Using defaults.', err);
  }
  return defaultPrefs;
};

export const NotificationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<NotificationPrefs>(() => readPrefsFromStorage());
  const [userId, setUserId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const supabase = getSupabaseClient();
    let cancelled = false;

    const loadPreferences = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError) throw authError;
        if (!user) {
          setInitializing(false);
          return;
        }
        if (cancelled) return;
        setUserId(user.id);

        const { data, error } = await supabase
          .from('notification_preferences')
          .select('email_enabled, sms_enabled, preferred_channel')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          if (error.code !== 'PGRST116') {
            console.warn('Failed to load notification preferences row.', error);
          }
        } else if (data && !cancelled) {
          setPrefs({
            emailEnabled: data.email_enabled,
            smsEnabled: data.sms_enabled,
            preferredMethod: resolvePreferredMethod(
              data.email_enabled,
              data.sms_enabled,
              (data.preferred_channel as PreferredMethod) ?? null,
            ),
          });
        }
      } catch (err) {
        console.warn('Unable to fetch notification preferences from Supabase.', err);
      } finally {
        if (!cancelled) {
          setInitializing(false);
        }
      }
    };

    loadPreferences();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prefs));
    } catch (err) {
      console.warn('Failed to cache notification prefs locally.', err);
    }
  }, [prefs]);

  useEffect(() => {
    if (!userId || initializing) return;
    const supabase = getSupabaseClient();

    const persist = async () => {
      try {
        await supabase.from('notification_preferences').upsert(
          {
            user_id: userId,
            email_enabled: prefs.emailEnabled,
            sms_enabled: prefs.smsEnabled,
            preferred_channel: prefs.preferredMethod,
          },
          { onConflict: 'user_id' },
        );
      } catch (err) {
        console.warn('Failed to persist notification preferences.', err);
      }
    };

    persist();
  }, [prefs, userId, initializing]);

  const handleToggle = (type: 'email' | 'sms') => {
    setPrefs(prev => {
      const nextValue = type === 'email' ? !prev.emailEnabled : !prev.smsEnabled;
      const nextPrefs: NotificationPrefs =
        type === 'email'
          ? { ...prev, emailEnabled: nextValue }
          : { ...prev, smsEnabled: nextValue };

      if (!nextValue && prev.preferredMethod === type) {
        nextPrefs.preferredMethod = type === 'email'
          ? (nextPrefs.smsEnabled ? 'sms' : null)
          : (nextPrefs.emailEnabled ? 'email' : null);
      } else if (nextValue && !nextPrefs.preferredMethod) {
        nextPrefs.preferredMethod = type;
      }

      return nextPrefs;
    });
  };

  const handlePreferredChange = (method: PreferredMethod) => {
    if (!method) return;
    if ((method === 'email' && !prefs.emailEnabled) || (method === 'sms' && !prefs.smsEnabled)) {
      return;
    }
    setPrefs(prev => ({ ...prev, preferredMethod: method }));
  };

  const preferredDisabled = !prefs.emailEnabled && !prefs.smsEnabled;

  return (
    <div className="flex min-h-screen flex-col bg-[#FFFCEF] text-[#2C2731]">
      <header className="flex h-16 items-center border-b border-[#FFFCEF]/20 bg-[#2C2731] px-4 text-[#FFFCEF]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#FFFCEF]/30 text-xl transition hover:bg-[#FFFCEF]/10"
          aria-label="Go back"
        >
          {'\u2190'}
        </button>
        <div className="flex flex-1 justify-center font-recoleta text-lg tracking-wide">
          Notification Settings
        </div>
        <div className="w-10" aria-hidden />
      </header>

      <main className="flex flex-1 justify-center overflow-y-auto px-4 py-6">
        <div className="flex w-full max-w-[520px] flex-col gap-6">
          <section className="rounded-[32px] border border-[#2C2731]/20 bg-white/90 p-6 text-[#2C2731] shadow-[0_25px_80px_-40px_rgba(15,8,27,0.4)]">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#707C8B]">Overview</p>
            <h1 className="mt-3 font-recoleta text-[30px] leading-tight text-[#2C2731]">Stay in the loop</h1>
            <p className="mt-2 text-[15px] leading-relaxed text-[#214D3C]">
              Choose how you hear about sessions, reminders, and important updates from your guide.
            </p>
          </section>

          <section className="rounded-[28px] border border-[#2C2731]/10 bg-white p-5 text-[#2C2731] shadow-[0_25px_60px_-35px_rgba(15,8,27,0.35)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#707C8B]">
                  Channels
                </p>
                <h2 className="mt-1 text-xl font-semibold">Contact methods</h2>
              </div>
              <span className="rounded-full border border-[#2C2731]/15 bg-[#FFFCEF] px-3 py-1 text-xs font-semibold text-[#2C2731]">
                Auto-saves
              </span>
            </div>

            <div className="mt-5 space-y-4">
              <label className="flex items-center justify-between rounded-2xl border border-[#2C2731]/10 bg-[#FBF2CF] px-4 py-3">
                <div>
                  <p className="text-base font-semibold">Email updates</p>
                  <p className="text-sm text-[#214D3C]">Session reminders, guide messages, and offers.</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('email')}
                  className={[
                    'relative flex h-8 w-14 items-center rounded-full transition',
                    prefs.emailEnabled ? 'bg-[#2C2731]' : 'bg-[#C9C3B3]',
                  ].join(' ')}
                  aria-pressed={prefs.emailEnabled}
                  aria-label="Toggle email notifications"
                >
                  <span
                    className={[
                      'inline-block h-6 w-6 rounded-full bg-white transition',
                      prefs.emailEnabled ? 'translate-x-6' : 'translate-x-1',
                    ].join(' ')}
                  />
                </button>
              </label>

              <label className="flex items-center justify-between rounded-2xl border border-[#2C2731]/10 bg-[#FBF2CF] px-4 py-3">
                <div>
                  <p className="text-base font-semibold">SMS alerts</p>
                  <p className="text-sm text-[#214D3C]">Quick nudges about booking changes or upcoming sessions.</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle('sms')}
                  className={[
                    'relative flex h-8 w-14 items-center rounded-full transition',
                    prefs.smsEnabled ? 'bg-[#2C2731]' : 'bg-[#C9C3B3]',
                  ].join(' ')}
                  aria-pressed={prefs.smsEnabled}
                  aria-label="Toggle SMS notifications"
                >
                  <span
                    className={[
                      'inline-block h-6 w-6 rounded-full bg-white transition',
                      prefs.smsEnabled ? 'translate-x-6' : 'translate-x-1',
                    ].join(' ')}
                  />
                </button>
              </label>
            </div>
          </section>

          <section className="rounded-[28px] border border-[#2C2731]/10 bg-white p-5 text-[#2C2731] shadow-[0_25px_60px_-35px_rgba(15,8,27,0.35)]">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#707C8B]">Preference</p>
            <h2 className="mt-1 text-xl font-semibold">Primary contact method</h2>
            <p className="mt-1 text-sm text-[#214D3C]">
              We’ll reach out here first. Other enabled channels still receive backups.
            </p>

            <div className="mt-4 space-y-3">
              {(['email', 'sms'] as PreferredMethod[]).map(method => (
                <label
                  key={method}
                  className={[
                    'flex items-center justify-between rounded-2xl border px-4 py-3',
                    prefs.preferredMethod === method
                      ? 'border-[#2C2731] bg-[#FFFCEF]'
                      : 'border-[#2C2731]/15 bg-white',
                    ((method === 'email' && !prefs.emailEnabled) || (method === 'sms' && !prefs.smsEnabled))
                      ? 'opacity-40'
                      : '',
                  ].join(' ')}
                >
                  <div>
                    <p className="text-base font-semibold capitalize">{method}</p>
                    <p className="text-sm text-[#214D3C]">
                      {method === 'email'
                        ? 'Best for detailed updates or receipts.'
                        : 'Great for timely reminders on the go.'}
                    </p>
                  </div>
                  <input
                    type="radio"
                    name="preferred-method"
                    value={method ?? ''}
                    checked={prefs.preferredMethod === method}
                    disabled={(method === 'email' && !prefs.emailEnabled) || (method === 'sms' && !prefs.smsEnabled)}
                    onChange={() => handlePreferredChange(method)}
                    className="h-5 w-5 accent-[#2C2731]"
                  />
                </label>
              ))}
            </div>

            {preferredDisabled && (
              <div className="mt-3 rounded-xl border border-[#B24E4E]/30 bg-[#FCEDEA] px-3 py-2 text-sm text-[#8C2F2F]">
                Turn on at least one channel above to select a preferred method.
              </div>
            )}
          </section>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-4 rounded-full border border-[#2C2731]/20 bg-white px-5 py-3 text-sm font-semibold text-[#2C2731] transition hover:-translate-y-0.5 hover:border-[#2C2731]"
          >
            Done
          </button>
        </div>
      </main>
    </div>
  );
};

export default NotificationSettingsPage;
